<script setup>
import FooterLudwig from '@/Layouts/Partials/FooterLudwig.vue'
import FooterRahoma from '@/Layouts/Partials/FooterRahoma.vue'
import { PhoneIcon, EnvelopeIcon } from '@heroicons/vue/24/outline'
import { usePage } from '@inertiajs/vue3'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronRightIcon } from '@heroicons/vue/24/solid'

const page = usePage().props
</script>

<template>
    <footer class="overflow-hidden pt-16">
        <div class="border-t border-gray-200 bg-gray-50">
            <div class="section--width mt-5 mb-5 grid w-full grid-cols-12 gap-2 px-2 xl:mt-10 2xl:px-0">
                <div class="col-span-full xl:col-span-2">
                    <Link
                        :href="route('home.index')"
                        aria-label="domů"
                    >
                        <img
                            class="size-24 object-contain xl:h-48 xl:w-auto"
                            src="/src/logo/kamzajitolomouc-logo.png"
                            alt="Kam zajít Olomouc"
                            loading="lazy"
                        />
                    </Link>
                </div>

                <div class="col-span-full mt-5 xl:col-span-5 xl:mt-0">
                    <h3 class="font-roboto-slab text-left text-2xl font-bold text-gray-900 uppercase">
                        Chybí tu váš podnik ?
                    </h3>

                    <div class="mt-5 flex flex-col items-start justify-center space-y-4 xl:w-fit xl:items-start">
                        <div class="flex items-center space-x-4">
                            <div>
                                <PhoneIcon class="size-6 text-red-500" />
                            </div>

                            <div>
                                <a
                                    class="text-lg font-semibold text-gray-800"
                                    href="tel:420607247034"
                                    aria-label="Telefonní číslo"
                                >
                                    +420 607 247 034
                                </a>

                                <p class="text-[14px] text-gray-500">Po - Ne 8 - 18h</p>
                            </div>
                        </div>

                        <div class="flex items-center space-x-4">
                            <div>
                                <EnvelopeIcon class="size-6 text-red-500" />
                            </div>

                            <div>
                                <a
                                    class="text-lg font-semibold text-gray-800"
                                    href="mailto:info@kamzajitolomouc.cz"
                                    aria-label="Email"
                                >
                                    info@kamzajitolomouc.cz
                                </a>
                            </div>
                        </div>

                        <div
                            class="slower-animation flex cursor-pointer items-center rounded-xl border-2 border-red-500 bg-red-500 px-4 py-2 text-center whitespace-nowrap hover:bg-red-400"
                        >
                            <Link
                                :href="route('business-demands.create')"
                                class="inline-flex items-center px-3 text-base font-bold text-white"
                                aria-label="Chci abyste mě kontaktovali"
                            >
                                Chci abyste mě kontaktovali
                            </Link>
                        </div>
                    </div>
                </div>

                <div class="col-span-full mt-10 xl:col-span-5 xl:mt-0">
                    <div class="xl:ml-auto xl:w-9/12">
                        <h3 class="font-roboto-slab text-left text-2xl font-bold text-gray-900 uppercase">
                            Nezapomeň nás sledovat
                        </h3>
                        <div class="mt-1 grid grid-cols-4 rounded-xl border border-gray-200 bg-white p-1 xl:mt-5">
                            <div class="faster-animation rounded-lg hover:bg-gray-200">
                                <a
                                    class="flex w-full justify-center p-2"
                                    href="https://www.instagram.com/kamzajitolomouc"
                                    aria-label="Instagram - kamzajitolomouc"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        class="size-6 object-cover xl:size-8"
                                        src="/src/socials/instagram.webp"
                                        alt="Instragram ikona"
                                        loading="lazy"
                                    />
                                </a>
                            </div>

                            <div class="faster-animation rounded-lg hover:bg-gray-200">
                                <a
                                    class="flex w-full justify-center p-2"
                                    href="https://www.facebook.com/kamzajitolomouc"
                                    aria-label="Facebook - kamzajitolomouc"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        class="size-6 object-cover xl:size-8"
                                        src="/src/socials/facebook.webp"
                                        alt="Facebook ikona"
                                        loading="lazy"
                                    />
                                </a>
                            </div>

                            <div class="faster-animation rounded-lg hover:bg-gray-200">
                                <a
                                    class="flex w-full justify-center p-2"
                                    href="https://www.youtube.com/@kamzajitolomouc"
                                    aria-label="Youtube - kamzajitolomouc"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        class="size-6 object-cover xl:size-8"
                                        src="/src/socials/youtube.webp"
                                        alt="Instragram ikona"
                                        loading="lazy"
                                    />
                                </a>
                            </div>

                            <div class="faster-animation rounded-lg hover:bg-gray-200">
                                <a
                                    class="flex w-full justify-center p-2"
                                    href="https://www.tiktok.com/@kamzajitolomouc_?lang=en"
                                    aria-label="Instagram - kamzajitolomouc"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        class="size-6 object-cover xl:size-8"
                                        src="/src/socials/tiktok.webp"
                                        alt="Instragram ikona"
                                        loading="lazy"
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <div>
                    <img
                        src="/src/footer/kam-zajit-olomouc-footer.png"
                        alt="kam-zajit-olomouc-footer"
                        loading="lazy"
                    />
                </div>

                <div class="bg-[#1D1D1B] px-2 2xl:px-0">
                    <div
                        class="relative -top-0 flex w-full flex-col items-center justify-between space-y-6 lg:flex-row lg:space-y-0 xl:-top-10 2xl:-top-20"
                    >
                        <div
                            class="section--width hidden w-full flex-col items-start justify-between space-y-5 px-2 lg:flex lg:flex-row lg:space-y-0 xl:px-0"
                        >
                            <ul class="h-full space-y-2">
                                <li
                                    class="font-roboto-slab mb-2 text-xl font-bold text-white uppercase xl:mb-5 xl:text-2xl"
                                >
                                    Pro návštěvníky
                                </li>

                                <li>
                                    <Link
                                        :href="route('businesses.index')"
                                        class="text-[18px] text-white hover:underline"
                                        aria-label="Podniky"
                                    >
                                        Podniky
                                    </Link>
                                </li>

                                <li>
                                    <Link
                                        :href="route('services.index')"
                                        class="text-[18px] text-white hover:underline"
                                        aria-label="Služby"
                                    >
                                        Služby
                                    </Link>
                                </li>

                                <li>
                                    <Link
                                        :href="route('blogs.index')"
                                        class="text-[18px] text-white hover:underline"
                                        aria-label="Blogy"
                                    >
                                        Blogy
                                    </Link>
                                </li>

                                <li>
                                    <Link
                                        :href="route('about.index')"
                                        class="text-[18px] text-white hover:underline"
                                        aria-label="O nás"
                                    >
                                        O nás
                                    </Link>
                                </li>
                            </ul>

                            <ul class="h-full space-y-2">
                                <li
                                    class="font-roboto-slab mb-2 text-xl font-bold text-white uppercase xl:mb-5 xl:text-2xl"
                                >
                                    Nejoblíbenější katagorie podniků
                                </li>

                                <li
                                    v-for="category in page.featuredBusinessCategories"
                                    :key="category.id"
                                >
                                    <Link
                                        :href="route('businesses.category-index', category.slug)"
                                        class="text-[18px] text-white hover:underline"
                                        :aria-label="'Podniky ' + category.name"
                                    >
                                        {{ category.name }}
                                    </Link>
                                </li>
                            </ul>

                            <ul class="h-full space-y-2">
                                <li
                                    class="font-roboto-slab mb-2 text-xl font-bold text-white uppercase xl:mb-5 xl:text-2xl"
                                >
                                    Nejoblíbenější katagorie služeb
                                </li>

                                <li
                                    v-for="category in page.featuredServiceCategories"
                                    :key="category.id"
                                >
                                    <Link
                                        :href="route('services.category-index', category.slug)"
                                        class="text-[18px] text-white hover:underline"
                                        :aria-label="'Služby ' + category.name"
                                    >
                                        {{ category.name }}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div
                            class="section--width flex w-full flex-col items-start justify-between space-y-1 px-2 lg:hidden"
                        >
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="flex w-full flex-row items-center justify-between rounded-lg bg-[#242422] py-3 pr-2 pl-4"
                                >
                                    <span class="font-roboto-slab text-left text-lg font-bold text-white uppercase">
                                        Pro návštěvníky
                                    </span>

                                    <ChevronRightIcon
                                        :class="open && 'rotate-90 transform'"
                                        class="size-6 text-white"
                                    />
                                </DisclosureButton>

                                <transition
                                    enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-out"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0"
                                >
                                    <DisclosurePanel
                                        as="ul"
                                        class="!mt-2 space-y-2 pl-2 text-gray-500"
                                    >
                                        <li>
                                            <Link
                                                :href="route('businesses.index')"
                                                class="text-[16px] text-white hover:underline"
                                                aria-label="Podniky"
                                            >
                                                Podniky
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                :href="route('services.index')"
                                                class="text-[16px] text-white hover:underline"
                                                aria-label="Služby"
                                            >
                                                Služby
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                :href="route('blogs.index')"
                                                class="text-[16px] text-white hover:underline"
                                                aria-label="Blogy"
                                            >
                                                Blogy
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                :href="route('about.index')"
                                                class="text-[16px] text-white hover:underline"
                                                aria-label="O nás"
                                            >
                                                O nás
                                            </Link>
                                        </li>
                                    </DisclosurePanel>
                                </transition>
                            </Disclosure>

                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="flex w-full flex-row items-center justify-between rounded-lg bg-[#242422] py-3 pr-2 pl-4"
                                >
                                    <span class="font-roboto-slab text-left text-lg font-bold text-white uppercase">
                                        Nejoblíbenější katagorie podniků
                                    </span>

                                    <ChevronRightIcon
                                        :class="open && 'rotate-90 transform'"
                                        class="size-6 text-white"
                                    />
                                </DisclosureButton>

                                <transition
                                    enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-out"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0"
                                >
                                    <DisclosurePanel
                                        as="ul"
                                        class="!mt-2 space-y-2 pl-2 text-gray-500"
                                    >
                                        <li
                                            v-for="category in page.featuredBusinessCategories"
                                            :key="category.id"
                                        >
                                            <Link
                                                :href="route('businesses.category-index', category.slug)"
                                                class="text-[16px] text-white hover:underline"
                                                :aria-label="'Podniky ' + category.name"
                                            >
                                                {{ category.name }}
                                            </Link>
                                        </li>
                                    </DisclosurePanel>
                                </transition>
                            </Disclosure>

                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="flex w-full flex-row items-center justify-between rounded-lg bg-[#242422] py-3 pr-2 pl-4"
                                >
                                    <span class="font-roboto-slab text-left text-lg font-bold text-white uppercase">
                                        Nejoblíbenější katagorie služeb
                                    </span>

                                    <ChevronRightIcon
                                        :class="open && 'rotate-90 transform'"
                                        class="size-6 text-white"
                                    />
                                </DisclosureButton>

                                <transition
                                    enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-out"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0"
                                >
                                    <DisclosurePanel
                                        as="ul"
                                        class="!mt-2 space-y-2 pl-2 text-gray-500"
                                    >
                                        <li
                                            v-for="category in page.featuredServiceCategories"
                                            :key="category.id"
                                        >
                                            <Link
                                                :href="route('services.category-index', category.slug)"
                                                class="text-[16px] text-white hover:underline"
                                                :aria-label="'Služby ' + category.name"
                                            >
                                                {{ category.name }}
                                            </Link>
                                        </li>
                                    </DisclosurePanel>
                                </transition>
                            </Disclosure>
                        </div>
                    </div>

                    <div
                        class="flex w-full flex-col items-center justify-between space-y-6 py-5 lg:flex-row lg:space-y-0"
                    >
                        <div class="relative flex w-full items-center justify-center space-x-24 xl:space-x-16">
                            <FooterLudwig />
                            <FooterRahoma />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>
